import React, { useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useUserContext } from '../../../common/utils/context';
import { getUserName } from '../../../common/utils/user/getUserName';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Text, UserAvatar } from '../../components';
import { Card, Screen, SecondaryButton } from '../../design/components';
import { TextInputEditInPlace } from '../../components/input';
import withMediaPicker from '../../hocs/withMediaPicker';

const ProfileViewBase = ({ captureMedia, capturedMediaLink }) => {
	const styles = useThemedStyles(createStyles);
	const { user, modifyUser } = useUserContext();
	const processedPhotoUrl = useRef(null);

	useEffect(() => {
		if (!capturedMediaLink || processedPhotoUrl.current === capturedMediaLink) {
			return;
		}

		processedPhotoUrl.current = capturedMediaLink;
		modifyUser({ profilePhotoUrl: capturedMediaLink });
	}, [capturedMediaLink, modifyUser]);

	const removePhoto = useCallback(() => {
		modifyUser({ profilePhotoUrl: null });
	}, [modifyUser]);

	if (!user) {
		return null;
	}

	const userName = getUserName(user);

	return (
		<Screen
			backLabel="My account"
			heading="Profile"
			subheading="Update your photo and contact details. Changes are saved automatically."
		>
			<Card style={styles.avatarSection}>
				<UserAvatar
					name={userName}
					photoUrl={user.profilePhotoUrl}
					size={100}
				/>
				<View style={styles.avatarActions}>
					<SecondaryButton
						label="Upload photo"
						icon="camera"
						onPress={captureMedia}
					/>
					{
						Boolean(user.profilePhotoUrl) && (
							<SecondaryButton
								label="Remove photo"
								icon="trash-2"
								variant="tonal"
								onPress={removePhoto}
							/>
						)
					}
				</View>
			</Card>

			<View style={styles.fields}>
				<View style={styles.fieldGroup}>
					<Text style={styles.fieldLabel}>Email address</Text>
					<Text style={styles.fieldHint}>
						This field cannot be changed; it is used to allow the user to login.
					</Text>
					<Text style={styles.readonlyValue}>{user.username}</Text>
				</View>

				<View style={styles.fieldGroup}>
					<Text style={styles.fieldLabel}>First name</Text>
					<TextInputEditInPlace
						field="firstname"
						value={user.firstname || ''}
						placeholder="First name"
						onConfirm={modifyUser}
					/>
				</View>

				<View style={styles.fieldGroup}>
					<Text style={styles.fieldLabel}>Last name</Text>
					<TextInputEditInPlace
						field="lastname"
						value={user.lastname || ''}
						placeholder="Last name"
						onConfirm={modifyUser}
					/>
				</View>

				<View style={styles.fieldGroup}>
					<Text style={styles.fieldLabel}>Phone number</Text>
					<TextInputEditInPlace
						field="phone"
						value={user.phone || ''}
						placeholder="Phone number"
						onConfirm={modifyUser}
					/>
				</View>
			</View>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, radius, space, touch, type } }) => ({
	avatarSection: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.lg,
	},
	avatarActions: {
		flex: 1,
		gap: space.sm,
		alignItems: 'flex-start',
	},
	fields: {
		gap: space.gutter,
	},
	fieldGroup: {
		gap: 6,
	},
	fieldLabel: {
		...type.fieldLabel,
		color: colors.text,
	},
	fieldHint: {
		...type.secondary,
		color: colors.textSecondary,
	},
	readonlyValue: {
		...type.rowText,
		minHeight: touch.min,
		paddingVertical: 12,
		paddingHorizontal: 14,
		borderRadius: radius.card,
		backgroundColor: colors.disabledBg,
		color: colors.text,
	},
});

export default withMediaPicker(ProfileViewBase);
